import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  useRecordContext,
  TopToolbar,
  ListButton,
} from 'react-admin';
import { Card, CardContent, Grid, Typography, Box, Chip } from '@mui/material';

const TransactionShowActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);

const TransactionTitle = () => {
  const record = useRecordContext();
  return <span>Transaction: {record?.transaction_id}</span>;
};

export const TransactionShow = () => {
  return (
    <Show actions={<TransactionShowActions />} title={<TransactionTitle />}>
      <SimpleShowLayout>
        <TransactionDetailsSection />
      </SimpleShowLayout>
    </Show>
  );
};

const TransactionDetailsSection = () => {
  const record = useRecordContext();

  if (!record) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'prize':
        return 'primary';
      case 'bet':
        return 'info';
      case 'refund':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Transaction Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <InfoRow label="Transaction ID" value={record.transaction_id} />
              <InfoRow
                label="Type"
                value={
                  <Chip
                    label={record.transaction_type?.toUpperCase()}
                    color={getTypeColor(record.transaction_type)}
                    size="small"
                  />
                }
              />
              <InfoRow
                label="Status"
                value={
                  <Chip
                    label={record.status?.toUpperCase()}
                    color={getStatusColor(record.status)}
                    size="small"
                  />
                }
              />
              <InfoRow
                label="Amount"
                value={`$${formatNumber(record.amount_usd)} (${record.amount} ${
                  record.currency
                })`}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User & Chat Details
            </Typography>
            <Box sx={{ mt: 2 }}>
              <InfoRow label="User ID" value={record.user_id} />
              <InfoRow
                label="Username"
                value={record.username ? `@${record.username}` : 'N/A'}
              />
              <InfoRow label="Chat ID" value={record.chat_id} />
              <InfoRow label="Chat Title" value={record.chat_title || 'N/A'} />
              {record.event_id && <InfoRow label="Event ID" value={record.event_id} />}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Timeline
            </Typography>
            <Box sx={{ mt: 2 }}>
              <InfoRow
                label="Created At"
                value={new Date(record.created_at).toLocaleString()}
              />
              {record.paid_at && (
                <InfoRow
                  label="Paid At"
                  value={new Date(record.paid_at).toLocaleString()}
                />
              )}
              {record.failed_at && (
                <InfoRow
                  label="Failed At"
                  value={new Date(record.failed_at).toLocaleString()}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {record.xrocket_transfer_id && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                xRocket Details
              </Typography>
              <Box sx={{ mt: 2 }}>
                <InfoRow
                  label="Transfer ID"
                  value={record.xrocket_transfer_id}
                />
                {record.error_message && (
                  <InfoRow label="Error Message" value={record.error_message} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {record.notes && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                {record.notes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

// Helper components
const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num || 0);
};
